import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import {
  FaTimes, FaTrophy, FaAward, FaMedal, FaCertificate, FaBriefcase,
  FaExternalLinkAlt, FaDownload, FaGoogle, FaMicrosoft, FaLinkedin,
  FaBuilding, FaChevronLeft, FaChevronRight, FaEye, FaLayerGroup, FaArrowRight
} from 'react-icons/fa';
import './Certificates.css';

/* ------------------------------------------------------------------ */
/*  DATA  (images: public/certificates/<file>.jpg  ya .png  ya .pdf)   */
/* ------------------------------------------------------------------ */
const certifications = [
  { id: 1,  name: 'Full Stack Development',          issuer: 'Infosys Springboard',      icon: <FaTrophy />,      color: '#FFD700', category: 'Development', file: 'full-stack-infosys' },
  { id: 2,  name: 'Front End Development',           issuer: 'SkillUp',                  icon: <FaAward />,       color: '#3B82F6', category: 'Development', file: 'frontend-skillup' },
  { id: 3,  name: 'Cybersecurity Essentials',        issuer: 'Cisco Networking Academy', icon: <FaMedal />,       color: '#EF4444', category: 'Security',    file: 'cybersecurity-cisco' },
  { id: 4,  name: 'Cybersecurity',                   issuer: 'Government of India',      icon: <FaCertificate />, color: '#10B981', category: 'Security',    file: 'cybersecurity-gov' },
  { id: 5,  name: 'Software Testing',                issuer: 'Internshala',              icon: <FaAward />,       color: '#F59E0B', category: 'Testing',     file: 'software-testing-internshala' },
  { id: 6,  name: 'Photoshop (A Grade) ISO Certified', issuer: 'ISO 2015',               icon: <FaMedal />,       color: '#EC4899', category: 'Design',      file: 'photoshop-iso' },
  { id: 7,  name: 'Independent Travel Manager',      issuer: 'Dreamport',                icon: <FaCertificate />, color: '#14B8A6', category: 'Management',  file: 'travel-manager-dreamport' },
  { id: 8,  name: 'Digital Marketing',               issuer: 'HubSpot',                  icon: <FaTrophy />,      color: '#FF7A00', category: 'Marketing',   file: 'digital-marketing-hubspot' },
  { id: 9,  name: 'Digital Marketing',               issuer: 'Google',                   icon: <FaGoogle />,      color: '#4285F4', category: 'Marketing',   file: 'digital-marketing-google' },
  { id: 10, name: 'Android using Kotlin',            issuer: 'Infosys Springboard',      icon: <FaMedal />,       color: '#A855F7', category: 'Mobile',      file: 'android-kotlin-infosys' },
  { id: 11, name: 'Android Nougat Mobile Apps',      issuer: 'EDUCBA',                   icon: <FaCertificate />, color: '#3DDC84', category: 'Mobile',      file: 'android-educba' },
  { id: 12, name: 'Project Management',             issuer: 'LinkedIn',                 icon: <FaLinkedin />,    color: '#0A8DD4', category: 'Management',  file: 'project-management-linkedin' },
  { id: 13, name: 'Artificial Intelligence',         issuer: 'LinkedIn',                 icon: <FaAward />,       color: '#38BDF8', category: 'AI/ML',       file: 'ai-linkedin' },
  { id: 14, name: 'Generative AI',                   issuer: 'Microsoft',                icon: <FaMicrosoft />,   color: '#00A4EF', category: 'AI/ML',       file: 'generative-ai-microsoft' },
  { id: 15, name: 'Data Analyst',                    issuer: 'Google',                   icon: <FaGoogle />,      color: '#34A853', category: 'Data',        file: 'data-analyst-google' }
];

const internships = [
  { id: 1, name: 'Data Science and Machine Learning', issuer: 'Ybi Foundation',       duration: '3 Months', icon: <FaBriefcase />, color: '#FFD700', file: 'internship-ybi' },
  { id: 2, name: 'Cybersecurity Analyst',             issuer: 'Tata',                 duration: 'Virtual',  icon: <FaBriefcase />, color: '#3B82F6', file: 'internship-tata-cyber' },
  { id: 3, name: 'Data Visualization',                issuer: 'Tata',                 duration: 'Virtual',  icon: <FaBriefcase />, color: '#EF4444', file: 'internship-tata-data' },
  { id: 4, name: 'Excel Skills',                      issuer: 'JPMorgan Chase & Co.', duration: 'Virtual',  icon: <FaBuilding />,  color: '#10B981', file: 'internship-jpmorgan-excel' },
  { id: 5, name: 'Software Engineering',              issuer: 'JPMorgan Chase & Co.', duration: 'Virtual',  icon: <FaBuilding />,  color: '#A855F7', file: 'internship-jpmorgan-software' },
  { id: 6, name: 'Advanced Software Engineering',     issuer: 'Walmart Global Tech',  duration: 'Virtual',  icon: <FaBriefcase />, color: '#F59E0B', file: 'internship-walmart' },
  { id: 7, name: 'Full Stack Website Development',    issuer: 'EI System',            duration: '2 Months', icon: <FaBriefcase />, color: '#EC4899', file: 'internship-ei-system' },
  { id: 8, name: 'Agile Methodology',                 issuer: 'Cognizant',            duration: 'Virtual',  icon: <FaBuilding />,  color: '#14B8A6', file: 'internship-cognizant' }
];

const fileUrl = (file, ext) => `/certificates/${file}.${ext}`;

const ease = [0.22, 1, 0.36, 1];
const gridV = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const cardV = {
  hidden: { opacity: 0, y: 30, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease } }
};

/* certificate cards: each one animates when it scrolls into view */
const itemV = {
  hidden: { opacity: 0, y: 26, scale: 0.95 },
  show: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.5, delay: (i % 5) * 0.06, ease }
  })
};

/* ------------------------------------------------------------------ */
/*  Small pieces                                                       */
/* ------------------------------------------------------------------ */
const CountUp = ({ to }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / 1200, 1);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <>{n}</>;
};


const CertThumb = ({ item, no }) => {
  const [i, setI] = useState(0);
  const srcs = [fileUrl(item.file, 'jpg'), fileUrl(item.file, 'png')];
  return (
    <div className="cp-thumb">
      {i < srcs.length ? (
        <img src={srcs[i]} alt={item.name} loading="lazy" draggable="false" onError={() => setI(i + 1)} />
      ) : (
        <div className="cp-thumb-fallback"><span>{item.icon}</span></div>
      )}
      <div className="cp-thumb-shade" />
      <span className="cp-no">{String(no).padStart(2, '0')}</span>
      <span className="cp-thumb-tag">{item.category || item.duration}</span>
      <span className="cp-thumb-eye"><FaEye /></span>
    </div>
  );
};


const CertViewer = ({ item }) => {
  const [stage, setStage] = useState(0);
  const urls = [fileUrl(item.file, 'jpg'), fileUrl(item.file, 'png'), fileUrl(item.file, 'pdf')];
  const url = urls[stage];
  return (
    <>
      <div className="cp-view-body">
        {stage < 2 ? (
          <img src={url} alt={item.name} onError={() => setStage(stage + 1)} />
        ) : (
          <iframe src={`${url}#toolbar=0`} title={item.name} />
        )}
      </div>
      <div className="cp-view-actions">
        <a href={url} target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> Open in new tab</a>
        <a href={url} download><FaDownload /> Download</a>
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
const Certificates = () => {
  const [tab, setTab] = useState('certifications');
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState(null);

  const isCert = tab === 'certifications';
  const source = isCert ? certifications : internships;

  const categories = useMemo(() => {
    const map = {};
    certifications.forEach((c) => { map[c.category] = (map[c.category] || 0) + 1; });
    return Object.entries(map);
  }, []);

  const visible = isCert && filter !== 'All' ? source.filter((c) => c.category === filter) : source;

  const orgCount = useMemo(
    () => new Set([...certifications, ...internships].map((x) => x.issuer)).size,
    []
  );

  const stats = [
    { label: 'Certifications', value: certifications.length, icon: <FaCertificate />, c: '#64ffda' },
    { label: 'Internships', value: internships.length, icon: <FaBriefcase />, c: '#a78bfa' },
    { label: 'Organizations', value: orgCount, icon: <FaBuilding />, c: '#38bdf8' },
    { label: 'Skill Areas', value: categories.length, icon: <FaLayerGroup />, c: '#fbbf24' }
  ];

  const switchTab = (t) => {
    setTab(t);
    setFilter('All');
  };

  const idx = selected ? visible.findIndex((x) => x.file === selected.file) : -1;
  const go = (d) => {
    if (idx < 0 || visible.length < 2) return;
    setSelected(visible[(idx + d + visible.length) % visible.length]);
  };

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelected(null);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
   
  }, [selected, idx, visible.length]);

  const spot = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="cp-page">
        <div className="cp-blob cp-blob-1" />
        <div className="cp-blob cp-blob-2" />
        <div className="cp-blob cp-blob-3" />

        {/* ---------- header ---------- */}
        <motion.header
          className="cp-hero"
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
        >
          <AnimatePresence mode="wait">
            <motion.h1
              key={tab}
              className="cp-title"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.3 }}
            >
              <span>{isCert ? 'Certifications' : 'Internships'}</span>
            </motion.h1>
          </AnimatePresence>
          <span className="cp-underline" />
          <p>
            {isCert
              ? 'Professional certifications from leading tech companies and institutions.'
              : 'Hands-on experience through industry internships and job simulations.'}
          </p>
        </motion.header>

        {/* ---------- stats ---------- */}
        <motion.div
          className="cp-stats"
          variants={gridV}
          initial="hidden"
          animate="show"
        >
          {stats.map((s) => (
            <motion.div key={s.label} className="cp-stat" style={{ '--sc': s.c }} variants={cardV} whileHover={{ y: -4 }}>
              <span className="cp-stat-ico">{s.icon}</span>
              <div>
                <b><CountUp to={s.value} /></b>
                <small>{s.label}</small>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* ---------- toolbar: tabs + category chips in one row ---------- */}
        <div className="cp-toolbar">
          <div className="cp-tabs" role="tablist">
            {[
              { id: 'certifications', label: 'Certifications', icon: <FaCertificate />, n: certifications.length },
              { id: 'internships', label: 'Internships', icon: <FaBriefcase />, n: internships.length }
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`cp-tab ${tab === t.id ? 'on' : ''}`}
                onClick={() => switchTab(t.id)}
              >
                {tab === t.id && <motion.span layoutId="cp-pill" className="cp-pill" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />}
                <span className="cp-tab-in">{t.icon}<span className="cp-tab-label">{t.label}</span><em>{t.n}</em></span>
              </button>
            ))}
          </div>

          {isCert && (
            <div className="cp-chips" aria-label="Filter by category">
              {[['All', certifications.length], ...categories].map(([name, n]) => (
                <button key={name} type="button" className={filter === name ? 'on' : ''} onClick={() => setFilter(name)}>
                  {filter === name && (
                    <motion.span layoutId="cp-chip-pill" className="cp-chip-pill" transition={{ type: 'spring', stiffness: 420, damping: 34 }} />
                  )}
                  <span className="cp-chip-in">{name} <em>{n}</em></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ---------- grid ---------- */}
        <div key={`${tab}-${filter}`} className="cp-grid">
          {visible.map((item, n) => (
            <motion.article
              key={item.file}
              className="cp-card"
              style={{ '--cc': item.color }}
              variants={itemV}
              custom={n}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-30px' }}
              whileHover={{ y: -6, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
              onMouseMove={spot}
              onClick={() => setSelected(item)}
              role="button"
              tabIndex={0}
              aria-label={`View ${item.name}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelected(item);
                }
              }}
            >
              <span className="cp-card-bar" />
              <CertThumb item={item} no={n + 1} />
              <div className="cp-card-body">
                <span className="cp-badge">{item.icon}</span>
                <div className="cp-card-txt">
                  <h3>{item.name}</h3>
                  <p><FaBuilding /> <span>{item.issuer}</span></p>
                </div>
              </div>
              <div className="cp-card-foot">
                <span>View certificate</span>
                <FaArrowRight />
              </div>
            </motion.article>
          ))}
        </div>

        {/* ---------- modal ---------- */}
        <AnimatePresence>
          {selected && (
            <motion.div
              className="cp-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
            >
              <motion.div
                className="cp-modal"
                style={{ '--cc': selected.color }}
                initial={{ opacity: 0, y: 50, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 40, scale: 0.96 }}
                transition={{ type: 'spring', damping: 26, stiffness: 260 }}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="cp-modal-bar" />
                <button type="button" className="cp-close" onClick={() => setSelected(null)} aria-label="Close">
                  <FaTimes />
                </button>

                <div className="cp-modal-head">
                  <span className="cp-modal-ico">{selected.icon}</span>
                  <div>
                    <h2>{selected.name}</h2>
                    <p>{selected.issuer}</p>
                  </div>
                </div>

                <div className="cp-viewer">
                  {visible.length > 1 && (
                    <button type="button" className="cp-nav cp-prev" onClick={() => go(-1)} aria-label="Previous">
                      <FaChevronLeft />
                    </button>
                  )}
                  <CertViewer key={selected.file} item={selected} />
                  {visible.length > 1 && (
                    <button type="button" className="cp-nav cp-next" onClick={() => go(1)} aria-label="Next">
                      <FaChevronRight />
                    </button>
                  )}
                </div>

                <div className="cp-modal-foot">
                  <span>{idx + 1} / {visible.length}</span>
                  <span className="cp-tag">{selected.category || selected.duration}</span>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
};

export default Certificates;